import * as React from 'react';
import {
  calculateProgress,
  PrimaryActionButtonProps,
  primaryActionButtonProps,
  SecondaryActionButtonProps,
  secondaryActionButtonProps,
} from './utils';
import * as registrationMachine from '../machines/accountRegistration';
import lang from '../lang';
import {UseMachineType} from '../machines/useAccountRegistrationMachineType';

type Props = {
  registrationMachine: UseMachineType;
};

type ScreenProps = Props & {
  lang: typeof lang.registerNames;
  onFormSubmit: React.ComponentProps<'form'>['onSubmit'];
  progress: number;
  actionButtons: {
    primary: PrimaryActionButtonProps;
    secondary: SecondaryActionButtonProps;
  };
  firstnameInputProps: {
    value: string | null;
    onChange: (value: string) => unknown;
    onBlur: () => unknown;
    error: string | undefined;
  };
  lastnameInputProps: {
    value: string | null;
    onChange: (value: string) => unknown;
    onBlur: () => unknown;
    error: string | undefined;
  };
  cardNumberValidationError: string | undefined;
};

export type RegisterNamesScreenType = React.ComponentType<ScreenProps>;

export class RegisterNames extends React.Component<
  Props & {children: RegisterNamesScreenType}
> {
  submit = ({firstname, lastname}: any) => {
    const [, send] = this.props.registrationMachine;
    send({
      type: registrationMachine.RegistrationEvent.UPDATE_REGISTRATION,
      payload: {firstname, lastname},
    });
  };

  render() {
    const [
      {
        context,
        context: {navigator},
        matches,
      },
      send,
    ] = this.props.registrationMachine;

    const progress = calculateProgress({
      draftAccount: context.account,
      location: navigator.location,
    });

    const firstname =
      context.account.firstname ?? context.practiceUserRecord.firstname;
    const lastname =
      context.account.lastname ?? context.practiceUserRecord.lastname;

    const actionButtons = {
      primary: primaryActionButtonProps({
        children: lang.registerNames.buttons.primary.title,
        loading:
          matches({
            [registrationMachine.RegistrationState.nameVerification]:
              registrationMachine.RegistrationState.verifyingName,
          }) ||
          matches({
            [registrationMachine.RegistrationState.nameVerification]:
              registrationMachine.RegistrationState.committingRewardsCard,
          }),
        disabled: Boolean(
          !firstname ||
            !lastname ||
            context.validationErrors?.account?.firstname ||
            context.validationErrors?.account?.lastname ||
            context.validationErrors?.cardNumber
        ),
      }),
      secondary: secondaryActionButtonProps({
        children: lang.registerNames.buttons.secondary.title,
        onClick: () => navigator.goBack(),
        loading: false,
        disabled: false,
      }),
    };

    const renderProps: ScreenProps = {
      ...this.props,
      lang: lang.registerNames,
      onFormSubmit: e => {
        e.preventDefault();
        this.submit({firstname, lastname});
      },
      progress,
      actionButtons,
      firstnameInputProps: {
        value: firstname,
        onChange: value =>
          send({
            type: registrationMachine.RegistrationEvent.UPDATE_DATA,
            payload: {account: {firstname: value}},
          }),
        onBlur: () => {
          if (!firstname) {
            send({
              type: registrationMachine.RegistrationEvent.HAS_VALIDATION_ERRORS,
              errors: {
                account: {
                  firstname: lang.registerNames.firstnameInput.errors.blank,
                },
              },
            });
          }
        },
        error: context.validationErrors?.account?.firstname,
      },
      lastnameInputProps: {
        value: lastname,
        onChange: value => {
          send({
            type: registrationMachine.RegistrationEvent.UPDATE_DATA,
            payload: {account: {lastname: value}},
          });
        },
        onBlur: () => {
          if (!lastname) {
            send({
              type: registrationMachine.RegistrationEvent.HAS_VALIDATION_ERRORS,
              errors: {
                account: {
                  lastname: lang.registerNames.lastnameInput.errors.blank,
                },
              },
            });
          }
        },
        error: context.validationErrors?.account?.lastname,
      },
      cardNumberValidationError: context.validationErrors?.cardNumber,
    };

    const Screen = this.props.children;
    return <Screen {...renderProps} />;
  }
}
